/**
 * Wirkungen: Dampf, Flammen, Blasen, Spritzer, Bewegungsstriche, Hitze.
 *
 * Alle Wirkungen sind schleifenfest: Bei Zeit 0 und Zeit 1 sehen sie gleich
 * aus, weil sie mit ganzzahligen Umläufen arbeiten.
 */
import { fract, random, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { path } from '../paths.ts';
import { circle, group, line, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';

/** Aufsteigender Dampf in geschwungenen Fäden. */
export function steam(
  cx: number,
  baseY: number,
  t: number,
  options: { count?: number; spread?: number; height?: number; strength?: number; loops?: number } = {},
): SceneNode[] {
  const count = options.count ?? 3;
  const spread = options.spread ?? 16;
  const height = options.height ?? 30;
  const strength = options.strength ?? 1;
  const loops = options.loops ?? 2;
  const nodes: SceneNode[] = [];
  for (let index = 0; index < count; index += 1) {
    const phase = fract(t * loops + index / count);
    const opacity = Math.sin(phase * Math.PI) * strength;
    if (opacity <= 0.03) {
      continue;
    }
    const x = cx + (index - (count - 1) / 2) * spread + wave(phase, 1, index * 0.3) * 3;
    const y = baseY - phase * height;
    const d = path()
      .move(x, y)
      .curve(x + 6, y - 5, x - 6, y - 10, x, y - 16)
      .toString();
    nodes.push(shape(d, { stroke: 'deep', strokeWidth: STROKE.detail, opacity }));
  }
  return nodes;
}

/** Eine Flamme, die leicht flackert. */
export function flame(cx: number, baseY: number, size: number, t: number, seed = 0): SceneNode {
  const flicker = 1 + 0.12 * wave(t, 4, seed) + 0.06 * wave(t, 9, seed * 1.7);
  const height = size * flicker;
  const width = size * 0.42;
  const sway = wave(t, 3, seed) * size * 0.1;
  const outline = (w: number, h: number) =>
    path()
      .move(cx, baseY)
      .curve(cx - w * 1.1, baseY, cx - w * 0.8, baseY - h * 0.55, cx + sway, baseY - h)
      .curve(cx + w * 0.8, baseY - h * 0.55, cx + w * 1.1, baseY, cx, baseY)
      .close()
      .toString();
  return group([
    shape(outline(width, height), styles.flat('accent')),
    shape(outline(width * 0.5, height * 0.55), styles.flat('accentTint')),
  ]);
}

/** Reihe von Gasflammen, Stärke 0 bis 1. */
export function burnerFlames(
  cx: number,
  baseY: number,
  width: number,
  power: number,
  t: number,
): SceneNode[] {
  if (power <= 0.02) {
    return [];
  }
  const count = 5;
  const nodes: SceneNode[] = [];
  for (let index = 0; index < count; index += 1) {
    const x = cx - width / 2 + (width / (count - 1)) * index;
    nodes.push(flame(x, baseY, 6 + 10 * power, t, index * 1.3));
  }
  return nodes;
}

/** Wellige Oberfläche einer Flüssigkeit als offene Linie. */
export function surfaceLine(
  x: number,
  y: number,
  width: number,
  amplitude: number,
  t: number,
  cycles = 2,
): string {
  const builder = path().move(x, y + Math.sin(t * Math.PI * 2) * amplitude);
  const steps = 14;
  for (let index = 1; index <= steps; index += 1) {
    const px = x + (width / steps) * index;
    const py = y + Math.sin((index / steps) * Math.PI * 2 * cycles + t * Math.PI * 2) * amplitude;
    builder.line(px, py);
  }
  return builder.toString();
}

/** Kurze Bewegungsstriche, zum Beispiel hinter einem Schneebesen. */
export function motionLines(
  x: number,
  y: number,
  angleDeg: number,
  length = 10,
  count = 3,
  gap = 6,
  opacity = 1,
): SceneNode | null {
  if (opacity <= 0.03) {
    return null;
  }
  const radians = (angleDeg * Math.PI) / 180;
  const nx = -Math.sin(radians);
  const ny = Math.cos(radians);
  const nodes: SceneNode[] = [];
  for (let index = 0; index < count; index += 1) {
    const offset = (index - (count - 1) / 2) * gap;
    const sx = x + nx * offset;
    const sy = y + ny * offset;
    const scale = index === Math.floor(count / 2) ? 1 : 0.65;
    nodes.push(
      line(sx, sy, sx + Math.cos(radians) * length * scale, sy + Math.sin(radians) * length * scale, {
        stroke: 'deep',
        strokeWidth: STROKE.detail,
      }),
    );
  }
  return group(nodes, { opacity });
}

/**
 * Blasen in einer Flüssigkeit. Stärke 0 (keine) bis 1 (sprudelnd kochen).
 * Die Blasen steigen von unten auf und verschwinden an der Oberfläche.
 */
export function bubbles(
  x: number,
  surfaceY: number,
  width: number,
  depth: number,
  strength: number,
  t: number,
  seed = 7,
): SceneNode[] {
  if (strength <= 0.02) {
    return [];
  }
  const rng = random(seed);
  const count = Math.round(3 + strength * 9);
  const speed = 1 + Math.round(strength * 2);
  const nodes: SceneNode[] = [];
  for (let index = 0; index < count; index += 1) {
    const offset = rng();
    const column = rng();
    const size = 1.6 + rng() * 2.2 * (0.6 + strength * 0.6);
    const phase = fract(t * speed + offset);
    const bx = x + 6 + column * (width - 12) + wave(phase, 1, offset) * 1.5;
    const by = surfaceY + depth - phase * depth;
    const opacity = phase > 0.88 ? (1 - phase) / 0.12 : 1;
    nodes.push(circle(bx, by, size, { fill: 'paper', stroke: 'ink', strokeWidth: STROKE.fine, opacity }));
  }
  return nodes;
}

/** Spritzer beim Anbraten: kleine Tropfen, die hochspringen. */
export function sizzle(
  cx: number,
  y: number,
  width: number,
  strength: number,
  t: number,
  seed = 3,
): SceneNode[] {
  if (strength <= 0.02) {
    return [];
  }
  const rng = random(seed);
  const nodes: SceneNode[] = [];
  const count = Math.round(3 + strength * 6);
  for (let index = 0; index < count; index += 1) {
    const offset = rng();
    const side = rng() - 0.5;
    const height = 10 + rng() * 14 * strength;
    const phase = fract(t * 3 + offset);
    const px = cx + side * width + side * phase * 14;
    const py = y - Math.sin(phase * Math.PI) * height;
    nodes.push(circle(px, py, 1.5, { fill: 'deep', stroke: 'none', opacity: 1 - phase * 0.6 }));
  }
  return nodes;
}

/** Wellenlinien für Hitze, zum Beispiel über einem Ofen. */
export function heatWaves(
  cx: number,
  baseY: number,
  t: number,
  strength: number,
  count = 3,
  spread = 30,
): SceneNode[] {
  if (strength <= 0.02) {
    return [];
  }
  const nodes: SceneNode[] = [];
  for (let index = 0; index < count; index += 1) {
    const phase = fract(t * 2 + index / count);
    const opacity = Math.sin(phase * Math.PI) * strength;
    if (opacity <= 0.03) {
      continue;
    }
    const x = cx + (index - (count - 1) / 2) * spread;
    const y = baseY - phase * 14;
    const d = path()
      .move(x - 9, y)
      .quad(x - 4.5, y - 5, x, y)
      .quad(x + 4.5, y + 5, x + 9, y)
      .toString();
    nodes.push(shape(d, { stroke: 'accent', strokeWidth: STROKE.detail, opacity }));
  }
  return nodes;
}

/** Fallende Krümel, Tropfen oder Mehl – von oben nach unten, schleifenfest. */
export function falling(
  x: number,
  topY: number,
  bottomY: number,
  width: number,
  count: number,
  t: number,
  options: { seed?: number; size?: number; loops?: number; tone?: Tone } = {},
): SceneNode[] {
  const rng = random(options.seed ?? 11);
  const loops = options.loops ?? 2;
  const size = options.size ?? 1.6;
  const nodes: SceneNode[] = [];
  for (let index = 0; index < count; index += 1) {
    const offset = rng();
    const column = rng();
    const phase = fract(t * loops + offset);
    const px = x + (column - 0.5) * width * (0.4 + phase * 0.6);
    const py = topY + (bottomY - topY) * phase;
    nodes.push(
      circle(px, py, size * (0.7 + rng() * 0.6), {
        fill: options.tone ?? 'deep',
        stroke: 'none',
      }),
    );
  }
  return nodes;
}
